import { useCallback, useMemo, useState } from 'react';
import { Outlet, createFileRoute, redirect, useLocation, useNavigate } from '@tanstack/react-router';
import { getDecodedWeaveToken, getWeaveToken, isWeaveTokenActive } from '@frontend/auth-helpers';
import { http } from '@frontend/fetch';
import { useWebSocket, useWebsocketEventPublisher } from '@frontend/websocket';
import type { WebsocketEventPayload } from '@frontend/websocket';
import { isDevBackend, getAirTrainerLocationId } from '../constants';
import { AppHeader } from '../shared/components/app-header/app-header';
import { Loader } from '../shared/components/loader/loader';
import { useAuthStore } from '../store/auth-store';
import { useQueueFilterStore } from '../store/queue-filter-store';
import { useThreadSelectionStore } from '../store/thread-selection-store';

const getWsApiUrl = () =>
  isDevBackend()
    ? 'wss://api.weavedev.net/websocket/connect/user'
    : 'wss://api.weaveconnect.com/websocket/connect/user';

const AuthLayout = () => {
  // ── WebSocket connection — uses the same useWebSocket hook as the Weave app ──
  const publisher = useWebsocketEventPublisher();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const wsOrgId = useMemo(() => (getDecodedWeaveToken() as any)?.org_id ?? '', []);
  const wsRefreshToken = useCallback(async () => {
    const token = getWeaveToken();
    if (!token || !isWeaveTokenActive()) {
      console.warn('[AIR Trainer WS] Token expired, redirecting to sign-in');
      useAuthStore.getState().logout();
      window.location.assign('/sign-in');
      return '';
    }
    return token;
  }, []);
  const wsOnMessage = useCallback(
    (payload: WebsocketEventPayload) => {
      if (!payload?.method) return;
      if (isDevBackend()) {
        console.debug('[AIR Trainer WS] Event received:', payload.method, payload);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      publisher.emit(payload.method as any, payload);
    },
    [publisher]
  );
  useWebSocket({
    onMessage: wsOnMessage,
    selectedLocationIds: [getAirTrainerLocationId()],
    refreshAuthToken: wsRefreshToken,
    websocketApi: getWsApiUrl(),
    selectedOrgId: wsOrgId,
    subscribeToOrg: false,
  });

  const [logoutLoading, setLogoutLoading] = useState(false);
  const firstName = useAuthStore((state) => state.firstName);
  const lastName = useAuthStore((state) => state.lastName);
  const email = useAuthStore((state) => state.email);
  const trainerLabel = firstName && lastName ? `${firstName} ${lastName}` : firstName || email || 'trainer';

  const locationFilterIds = useQueueFilterStore((state) => state.locationFilterIds);
  const locationFilterOptions = useQueueFilterStore((state) => state.locationFilterOptions);
  const setLocationFilterIds = useQueueFilterStore((state) => state.setLocationFilterIds);
  const setLocationFilterOptions = useQueueFilterStore((state) => state.setLocationFilterOptions);

  const handleSetLocationFilter = (ids: string[], options: { id: string; label: string }[]) => {
    setLocationFilterIds(ids);
    setLocationFilterOptions(options);
  };

  const navigate = useNavigate();
  const location = useLocation();

  const handleBrandClick = () => {
    // Clear selected thread
    useThreadSelectionStore.getState().selectThread('');
    // Navigate to dashboard if not already there
    if (location.pathname !== '/dashboard') {
      navigate({ to: '/dashboard' });
    }
  };

  return (
    <>
      <AppHeader
        trainerLabel={trainerLabel}
        trainerEmail={email}
        locationFilterIds={locationFilterIds}
        locationFilterOptions={locationFilterOptions}
        onSetLocationFilter={handleSetLocationFilter}
        logoutLoading={logoutLoading}
        onClickBrand={handleBrandClick}
        onLogout={async () => {
          setLogoutLoading(true);
          try {
            useAuthStore.getState().logout();
            window.location.assign('/sign-in');
          } finally {
            setLogoutLoading(false);
          }
        }}
      />
      <Outlet />
    </>
  );
};

const checkAuth = async () => {
  // Hydrate auth state from token in localStorage
  useAuthStore.getState().hydrate();
  const { isAuthenticated, hasRequiredAcl } = useAuthStore.getState();

  // No valid token → redirect to login
  if (!isAuthenticated) {
    throw redirect({ to: '/sign-in' });
  }

  // Authenticated but missing required AIR Trainer ACLs → clear token and redirect to login
  if (!hasRequiredAcl) {
    useAuthStore.getState().logout();
    throw redirect({ to: '/sign-in' });
  }

  http.setGlobalSkipValidation(true);
  http.setLocationIdHeader(getAirTrainerLocationId());
};

export const Route = createFileRoute('/_auth')({
  beforeLoad: checkAuth,
  pendingComponent: () => <Loader />,
  component: AuthLayout,
});
